import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

interface DayData {
  date: string;
  messages: number;
  users: number;
  interactions: number;
  whatsappMessages: number;
  webchatMessages: number;
  facebookMessages: number;
  voiceMessages: number;
}

interface Campaign {
  id: string;
  name?: string;
  status?: string;
  schedule?: any;
  createdAt?: number;
}

interface ReportData {
  bot: any;
  total: Record<string, number>;
  daily: DayData[];
  campaigns: Campaign[];
  agentTakeovers: number;
  agentReleases: number;
  totalContacts: number;
  facebookGenderBreakdown?: { male: number; female: number; unspecified: number };
  facebookLocaleBreakdown?: Record<string, number>;
  period: { days: number; startDate: string };
}

function fmtSchedule(s: any): string {
  if (!s) return 'manual';
  if (typeof s === 'string') return s;
  const days = Array.isArray(s.days) ? s.days.join(', ') : (s.days || '');
  const time = s.time || '';
  return [days, time].filter(Boolean).join(' @ ') || 'manual';
}

function fmtDate(ms?: number) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

function fmtNum(n?: number) {
  return (n || 0).toLocaleString();
}

// ── Simple SVG line chart ────────────────────────────────────────────────────
function LineChart({ data, field = 'messages' }: { data: DayData[]; field?: keyof DayData }) {
  if (!data.length) return null;
  const W = 800, H = 100, pad = 28;
  const vals = data.map(d => Number(d[field]) || 0);
  const max = Math.max(...vals, 1);
  const pts = vals.map((v, i) => {
    const x = pad + (i / Math.max(data.length - 1, 1)) * (W - pad * 2);
    const y = H - pad - (v / max) * (H - pad * 2);
    return [x, y] as [number, number];
  });
  const polyline = pts.map(([x, y]) => `${x},${y}`).join(' ');
  const area = `M ${pts[0]?.[0]},${H - pad} ` + pts.map(([x, y]) => `L ${x},${y}`).join(' ') + ` L ${pts[pts.length - 1]?.[0]},${H - pad} Z`;

  // X-axis labels: show every ~7th label
  const labelStep = Math.max(1, Math.floor(data.length / 7));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 120, display: 'block' }}>
      {/* Area fill */}
      <path d={area} fill="rgba(91,79,232,0.08)" />
      {/* Line */}
      <polyline points={polyline} fill="none" stroke="#5B4FE8" strokeWidth="2" strokeLinejoin="round" />
      {/* Dots on hover not possible without JS, so just show dots */}
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2.5" fill="#5B4FE8" />
      ))}
      {/* X labels */}
      {data.map((d, i) => i % labelStep === 0 && (
        <text key={d.date} x={pts[i]?.[0]} y={H - 4} textAnchor="middle" fontSize="9" fill="#475569">
          {d.date.slice(5)}
        </text>
      ))}
      {/* Y max label */}
      <text x={pad - 4} y={pad + 4} textAnchor="end" fontSize="9" fill="#475569">{fmtNum(max)}</text>
    </svg>
  );
}

// ── Horizontal stacked channel bar ──────────────────────────────────────────
function ChannelBreakdown({ total }: { total: Record<string, number> }) {
  const channels = [
    { key: 'whatsappMessages', label: 'WhatsApp', color: '#25d366' },
    { key: 'webchatMessages',  label: 'Web Chat', color: '#5B4FE8' },
    { key: 'facebookMessages', label: 'Facebook',  color: '#1877f2' },
    { key: 'voiceMessages',    label: 'Voice',     color: '#f59e0b' },
  ];
  const grand = channels.reduce((s, c) => s + (total[c.key] || 0), 0) || 1;
  return (
    <div>
      <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', height: 14, marginBottom: 10 }}>
        {channels.map(c => {
          const pct = ((total[c.key] || 0) / grand) * 100;
          return pct > 0 ? <div key={c.key} style={{ width: `${pct}%`, background: c.color }} title={`${c.label}: ${pct.toFixed(1)}%`} /> : null;
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {channels.map(c => (
          <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: c.color }} />
            <span style={{ color: '#94a3b8' }}>{c.label}</span>
            <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{fmtNum(total[c.key])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Facebook contacts by gender (aggregate only, never per-contact) ────────
function GenderBreakdown({ data }: { data: { male: number; female: number; unspecified: number } }) {
  const entries = [
    { key: 'male', label: 'Male', color: '#5B4FE8' },
    { key: 'female', label: 'Female', color: '#ec4899' },
    { key: 'unspecified', label: 'Unspecified', color: '#475569' },
  ] as const;
  const grand = entries.reduce((s, e) => s + (data[e.key] || 0), 0) || 1;
  return (
    <div>
      <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', height: 14, marginBottom: 10 }}>
        {entries.map(e => {
          const pct = ((data[e.key] || 0) / grand) * 100;
          return pct > 0 ? <div key={e.key} style={{ width: `${pct}%`, background: e.color }} title={`${e.label}: ${pct.toFixed(1)}%`} /> : null;
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {entries.map(e => (
          <div key={e.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: e.color }} />
            <span style={{ color: '#94a3b8' }}>{e.label}</span>
            <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{fmtNum(data[e.key])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Facebook contacts by locale (aggregate only, never per-contact) ────────
function LocaleBreakdown({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const grand = entries.reduce((s, [, n]) => s + n, 0) || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {entries.map(([locale, count]) => {
        const pct = (count / grand) * 100;
        return (
          <div key={locale} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 70, fontSize: 12, color: '#e2e8f0', fontWeight: 600 }}>{locale}</div>
            <div style={{ flex: 1, background: '#0d1117', borderRadius: 4, height: 10, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: '#5B4FE8' }} />
            </div>
            <div style={{ width: 40, fontSize: 12, color: '#94a3b8', textAlign: 'right' }}>{count}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── PDF print ────────────────────────────────────────────────────────────────
function printReport(data: ReportData, days: number) {
  const { bot, total, daily, campaigns, agentTakeovers, agentReleases, totalContacts } = data;
  const periodTotal = daily.reduce((s, d) => s + d.messages, 0);
  const periodUsers = daily.reduce((s, d) => s + d.users, 0);
  const avgPerDay = (periodTotal / (days || 1)).toFixed(1);
  const bestDay = [...daily].sort((a, b) => b.messages - a.messages)[0];

  const html = `<!DOCTYPE html><html><head><title>${bot.businessName} — ${days}-Day Report</title>
<style>
body{font-family:Arial,sans-serif;color:#111;padding:32px;max-width:900px;margin:0 auto}
h1{font-size:22px;margin:0 0 4px}
.sub{color:#555;font-size:13px;margin-bottom:28px}
.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:28px}
.card{border:1px solid #ddd;border-radius:8px;padding:14px}
.card-label{font-size:11px;color:#666;margin-bottom:4px;text-transform:uppercase;letter-spacing:.05em}
.card-value{font-size:24px;font-weight:700}
h2{font-size:15px;margin:24px 0 10px;padding-bottom:6px;border-bottom:1px solid #eee}
table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:24px}
th{text-align:left;padding:8px;background:#f5f5f5;border-bottom:2px solid #ddd}
td{padding:7px 8px;border-bottom:1px solid #eee}
.footer{margin-top:32px;font-size:11px;color:#999;border-top:1px solid #eee;padding-top:12px}
</style></head><body>
<h1>${bot.businessName} — Performance Report (Last ${days} Days)</h1>
<div class="sub">${bot.botType?.toUpperCase()} bot &nbsp;·&nbsp; Period: ${data.period.startDate} → ${new Date().toISOString().split('T')[0]} &nbsp;·&nbsp; Generated ${new Date().toLocaleString()}</div>

<div class="grid">
  <div class="card"><div class="card-label">Messages This Period</div><div class="card-value">${fmtNum(periodTotal)}</div></div>
  <div class="card"><div class="card-label">Users Reached</div><div class="card-value">${fmtNum(periodUsers)}</div></div>
  <div class="card"><div class="card-label">Total Contacts</div><div class="card-value">${fmtNum(totalContacts)}</div></div>
  <div class="card"><div class="card-label">Agent Takeovers</div><div class="card-value">${agentTakeovers}</div></div>
  <div class="card"><div class="card-label">Avg Messages/Day</div><div class="card-value">${avgPerDay}</div></div>
  <div class="card"><div class="card-label">Best Day</div><div class="card-value">${bestDay?.messages || 0}<span style="font-size:12px;font-weight:400;color:#666"> (${bestDay?.date || '—'})</span></div></div>
  <div class="card"><div class="card-label">All-Time Messages</div><div class="card-value">${fmtNum(total.totalMessages)}</div></div>
  <div class="card"><div class="card-label">Campaigns Total</div><div class="card-value">${campaigns.length}</div></div>
</div>

<h2>Daily Activity</h2>
<table>
  <tr><th>Date</th><th>Messages</th><th>Users</th><th>Interactions</th><th>WhatsApp</th><th>Web</th></tr>
  ${daily.map(d => `<tr><td>${d.date}</td><td>${d.messages}</td><td>${d.users}</td><td>${d.interactions}</td><td>${d.whatsappMessages}</td><td>${d.webchatMessages}</td></tr>`).join('')}
</table>

<h2>Campaigns (${campaigns.length})</h2>
<table>
  <tr><th>Name</th><th>Status</th><th>Schedule</th><th>Created</th></tr>
  ${campaigns.map(c => `<tr><td>${c.name || '—'}</td><td>${c.status || '—'}</td><td>${fmtSchedule(c.schedule)}</td><td>${fmtDate(c.createdAt)}</td></tr>`).join('')}
  ${campaigns.length === 0 ? '<tr><td colspan="4" style="color:#999">No campaigns</td></tr>' : ''}
</table>

<h2>Agent Activity</h2>
<table>
  <tr><th>Metric</th><th>Count</th></tr>
  <tr><td>Agent takeovers (period)</td><td>${agentTakeovers}</td></tr>
  <tr><td>Agent releases (period)</td><td>${agentReleases}</td></tr>
</table>

<div class="footer">ZimChat · ${bot.businessName} · Report generated ${new Date().toLocaleString()}</div>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 500);
}

// ── Main component ────────────────────────────────────────────────────────────
const PERIODS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const S: Record<string, React.CSSProperties> = {
  section: { background: '#15181f', borderRadius: 12, border: '1px solid #1e2330', padding: 20, marginBottom: 16 },
  sectionTitle: { color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 },
  statBox: { background: '#0d1117', border: '1px solid #1e2330', borderRadius: 10, padding: '14px 16px' },
  statVal: { fontSize: 24, fontWeight: 700, color: '#e2e8f0' },
  statLabel: { fontSize: 11, color: '#64748b', marginTop: 3 },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 12px', color: '#475569', fontWeight: 600, borderBottom: '1px solid #1e2330' },
  td: { padding: '8px 12px', borderBottom: '1px solid #0f1117', color: '#94a3b8' },
  tdPrimary: { padding: '8px 12px', borderBottom: '1px solid #0f1117', color: '#e2e8f0', fontWeight: 500 },
};

export default function ReportsPanel({ bot }: { bot: any }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!bot?.id) return;
    setLoading(true);
    setError('');
    setData(null);
    apiFetch(`/api/reports/${bot.id}?days=${days}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else setData(d);
      })
      .catch(() => setError('Failed to load report'))
      .finally(() => setLoading(false));
  }, [bot?.id, days]);

  const periodTotal  = data?.daily.reduce((s, d) => s + d.messages, 0) ?? 0;
  const periodUsers  = data?.daily.reduce((s, d) => s + d.users, 0) ?? 0;
  const avgPerDay    = data ? (periodTotal / (days || 1)).toFixed(1) : '—';
  const bestDay      = data ? [...(data.daily)].sort((a, b) => b.messages - a.messages)[0] : null;
  const activeDays   = data?.daily.filter(d => d.messages > 0).length ?? 0;

  return (
    <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ color: '#e2e8f0', fontSize: 18, fontWeight: 700 }}>{bot.businessName} — Report</div>
          <div style={{ color: '#475569', fontSize: 12, marginTop: 3 }}>Comprehensive performance breakdown</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => setDays(p.value)}
              style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', border: 'none',
                background: days === p.value ? '#5B4FE8' : '#1e2330',
                color: days === p.value ? 'white' : '#64748b',
                fontWeight: days === p.value ? 600 : 400 }}>
              {p.label}
            </button>
          ))}
          {data && (
            <button onClick={() => printReport(data, days)}
              style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', border: '1px solid #2d3548', background: '#1e2330', color: '#94a3b8' }}>
              Download PDF
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ color: '#475569', textAlign: 'center', padding: 60, fontSize: 14 }}>Loading report…</div>
      )}
      {error && (
        <div style={{ color: '#f87171', textAlign: 'center', padding: 40, fontSize: 13 }}>{error}</div>
      )}

      {data && !loading && (
        <>
          {/* Overview stat cards */}
          <div style={S.statGrid}>
            {[
              { label: `Messages (${days}d)`, val: fmtNum(periodTotal), color: '#5B4FE8' },
              { label: `Users Reached (${days}d)`, val: fmtNum(periodUsers), color: '#10b981' },
              { label: 'Total Contacts', val: fmtNum(data.totalContacts), color: '#06b6d4' },
              { label: 'Campaigns', val: data.campaigns.length, color: '#f59e0b' },
              { label: 'Agent Takeovers', val: data.agentTakeovers, color: data.agentTakeovers > 0 ? '#fb923c' : '#475569' },
              { label: 'Avg Messages/Day', val: avgPerDay, color: '#818cf8' },
              { label: 'Active Days', val: `${activeDays}/${days}`, color: '#94a3b8' },
              { label: 'All-Time Messages', val: fmtNum(data.total.totalMessages), color: '#64748b' },
            ].map(s => (
              <div key={s.label} style={S.statBox}>
                <div style={{ ...S.statVal, color: s.color }}>{s.val}</div>
                <div style={S.statLabel}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Daily messages chart */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Daily Messages — Last {days} Days</div>
            <LineChart data={data.daily} field="messages" />
            {bestDay && bestDay.messages > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                Best day: <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{bestDay.date}</span> with <span style={{ color: '#5B4FE8', fontWeight: 600 }}>{fmtNum(bestDay.messages)}</span> messages
              </div>
            )}
          </div>

          {/* Users reached chart */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Daily Users Reached</div>
            <LineChart data={data.daily} field="users" />
          </div>

          {/* Channel breakdown */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Channel Breakdown (All Time)</div>
            <ChannelBreakdown total={data.total} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginTop: 14 }}>
              {[
                { label: 'All-Time Users', val: fmtNum(data.total.totalUsers) },
                { label: 'All-Time Interactions', val: fmtNum(data.total.totalInteractions) },
                { label: 'WhatsApp', val: fmtNum(data.total.whatsappMessages) },
                { label: 'Web Chat', val: fmtNum(data.total.webchatMessages) },
              ].map(s => (
                <div key={s.label} style={{ background: '#0d1117', border: '1px solid #1e2330', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{s.val}</div>
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Facebook contacts by gender (aggregate only) */}
          {data.facebookGenderBreakdown && (data.facebookGenderBreakdown.male + data.facebookGenderBreakdown.female + data.facebookGenderBreakdown.unspecified > 0) && (
            <div style={S.section}>
              <div style={S.sectionTitle}>Facebook Contacts by Gender</div>
              <GenderBreakdown data={data.facebookGenderBreakdown} />
              <div style={{ fontSize: 11, color: '#475569', marginTop: 10 }}>
                Aggregate totals only, from Facebook profile data — never shown per-contact.
              </div>
            </div>
          )}

          {/* Facebook contacts by locale (aggregate only) */}
          {data.facebookLocaleBreakdown && Object.keys(data.facebookLocaleBreakdown).length > 0 && (
            <div style={S.section}>
              <div style={S.sectionTitle}>Facebook Contacts by Language</div>
              <LocaleBreakdown data={data.facebookLocaleBreakdown} />
              <div style={{ fontSize: 11, color: '#475569', marginTop: 10 }}>
                Aggregate totals only, from Facebook profile data — never shown per-contact.
              </div>
            </div>
          )}

          {/* Campaigns */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Campaigns ({data.campaigns.length})</div>
            {data.campaigns.length === 0 ? (
              <div style={{ color: '#475569', fontSize: 13 }}>No campaigns yet.</div>
            ) : (
              <table style={S.table}>
                <thead>
                  <tr>
                    {['Name', 'Status', 'Schedule', 'Created'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.campaigns.map(c => (
                    <tr key={c.id}>
                      <td style={S.tdPrimary}>{c.name || '—'}</td>
                      <td style={{ ...S.td, color: c.status === 'active' ? '#4ade80' : c.status === 'paused' ? '#fbbf24' : '#94a3b8' }}>
                        {c.status || 'draft'}
                      </td>
                      <td style={S.td}>{fmtSchedule(c.schedule)}</td>
                      <td style={S.td}>{fmtDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Agent activity */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Agent Activity (Last {days} Days)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {[
                { label: 'Takeovers', val: data.agentTakeovers, color: data.agentTakeovers > 0 ? '#fb923c' : '#475569',
                  sub: 'Times a human agent stepped in' },
                { label: 'Releases', val: data.agentReleases, color: '#4ade80',
                  sub: 'Times bot resumed after agent' },
                { label: 'Auto-handled', val: Math.max(0, periodTotal - data.agentTakeovers),
                  color: '#5B4FE8', sub: 'Messages handled by bot' },
              ].map(s => (
                <div key={s.label} style={{ background: '#0d1117', border: '1px solid #1e2330', borderRadius: 10, padding: '16px 18px' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{fmtNum(s.val)}</div>
                  <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{s.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Daily table */}
          <div style={S.section}>
            <div style={S.sectionTitle}>Daily Breakdown Table</div>
            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {['Date', 'Messages', 'Users', 'Interactions', 'WhatsApp', 'Web', 'Facebook', 'Voice'].map(h => (
                      <th key={h} style={{ ...S.th, fontSize: 11 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...data.daily].reverse().map(d => (
                    <tr key={d.date} style={{ opacity: d.messages === 0 ? 0.4 : 1 }}>
                      <td style={S.td}>{d.date}</td>
                      <td style={{ ...S.tdPrimary, color: d.messages > 0 ? '#5B4FE8' : '#475569' }}>{d.messages}</td>
                      <td style={S.td}>{d.users}</td>
                      <td style={S.td}>{d.interactions}</td>
                      <td style={S.td}>{d.whatsappMessages}</td>
                      <td style={S.td}>{d.webchatMessages}</td>
                      <td style={S.td}>{d.facebookMessages}</td>
                      <td style={S.td}>{d.voiceMessages}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
